import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect, useRef } from 'react'
import { getPedidos, getPedidosRaw, getBlingHistorico, getBlingContatos, saveBlingPedidoDetalhe, saveBlingLogisticaItem, getBlingLogisticaData, getBlingStatusOverrides, setBlingStatusOverride } from '../utils/storage'
import { BotaoAbrirAjuste, PainelAjusteFantasma } from '../components/AjustePanel'
import { criarPedidoAjuste } from '../data/ajustes'
import { DividirPedidoModal } from '../components/DividirPedidoModal'
import { dividirPedido, obterFilhos, statusAgregadoFilhos } from '../data/divisao'
import { Split, Wrench } from 'lucide-react'
import { useOrders } from '../hooks/useOrders'
import { gerarPDF } from '../utils/pdf'
import { exportarPedidoXlsx } from '../utils/excel'
import { StatusBadge } from '../components/common/Badge'
import { KANBAN_COLUNAS } from '../data/kanban'
import { ADICIONAIS_BOOLEAN, ADICIONAIS_QUANTIDADE } from '../data/adicionais'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { savePedido } from '../utils/storage'
import { abrirWhatsApp } from '../utils/whatsapp'
import { criarPedidoVendaBling, isBlingConnected, buscarPedidoBling, buscarPedidoBlingPorNumeroLoja } from '../utils/bling'
import { validarDocumento } from '../utils/validadores'
import { EditarClienteModal } from '../components/EditarClienteModal'
import { HistoricoPedido } from '../components/HistoricoPedido'
import { Pencil, Lock } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import {
  ArrowLeft, FileDown, Sheet, ChevronDown, Package, MessageCircle,
  RefreshCw, CheckCircle2, AlertCircle
} from 'lucide-react'

import { fmt, descricaoMovel, formatTelefone } from '../utils/fmt'

const FORMAS_LABEL  = { pix: 'Pix', dinheiro: 'Dinheiro', credito: 'Cartão de crédito', debito: 'Cartão de débito' }
const PRAZOS_LABEL  = { ato: 'Tudo no ato', '50_50': '50% pedido + 50% entrega' }
function fmtDate(d) {
  if (!d || d === '0000-00-00') return '—'
  try { return format(new Date(d + 'T12:00:00'), 'dd/MM/yyyy', { locale: ptBR }) } catch { return d }
}
// Formata ISO completo (com hora). Para pedidos cadastrados pelo Mini Deck,
// criadoEm = new Date().toISOString() — momento exato do cadastro.
function fmtDateTime(iso) {
  if (!iso) return '—'
  try { return format(new Date(iso), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) } catch { return iso }
}
function adicionaisAtivos(adicionais) {
  if (!adicionais) return []
  const r = []
  if (adicionais.puxadorTipo) {
    if (adicionais.puxadorTipo === 'Sem puxador') {
      r.push('Sem puxador')
    } else {
      const cor = adicionais.puxadorCor ? ` ${adicionais.puxadorCor}` : ''
      const qtd = adicionais.puxadorQtd > 0 ? ` (${adicionais.puxadorQtd}x)` : ''
      r.push(`Puxador ${adicionais.puxadorTipo}${cor}${qtd}`)
    }
  }
  ADICIONAIS_BOOLEAN.forEach(({ key, label }) => { if (adicionais[key]) r.push(label) })
  ADICIONAIS_QUANTIDADE.forEach(({ key, label }) => { if (adicionais[key] > 0) r.push(`${label}: ${adicionais[key]}`) })
  return r
}

// ── Detalhe de pedido importado do Bling ──────────────────────────────────────

function BlingOrderDetail({ blingId }) {
  const navigate = useNavigate()
  const [pedido,     setPedido]     = useState(null)
  const [carregando, setCarregando] = useState(true)
  const [erro,       setErro]       = useState(null)

  useEffect(() => {
    // 1. Monta dados iniciais: histórico + store de contatos (sem precisar de API)
    const raw         = getBlingHistorico().find(p => String(p.id) === blingId)
    const contatosMap = new Map(getBlingContatos().map(c => [String(c.id), c]))
    const contatoId   = String(raw?.contato?.id || '')
    const cont        = contatosMap.get(contatoId) || {}
    const endCont     = cont.endereco?.geral || {}
    const enderecoInicial = [endCont.endereco, endCont.numero, endCont.complemento, endCont.bairro, endCont.municipio]
      .filter(Boolean).join(', ')

    // Overrides locais (status, ajuste, histórico) — necessários pro painel de ajuste
    const idCompleto  = `bling-${blingId}`
    const log         = getBlingLogisticaData()[idCompleto] || {}
    const statusOver  = getBlingStatusOverrides()[idCompleto]

    if (raw) {
      const total = Number(raw.totalVenda ?? raw.total ?? 0)
      const prazo = raw.dataPrevista && raw.dataPrevista !== '0000-00-00' ? raw.dataPrevista : null
      setPedido({
        id:        idCompleto,
        numero:    raw.numeroLoja || `BL-${raw.numero}`,
        numeroErp: raw.numero,
        dataVenda: raw.data   || null,
        prazo,
        valorTotal: total,
        status:           statusOver || (prazo && prazo < '2026-04-20' ? 'ENTREGUE' : 'CADASTRADO'),
        ajuste:           log.ajuste            || null,
        historicoAjustes: log.historicoAjustes  || [],
        cliente: {
          nome:      raw.contato?.nome || cont.nome || '—',
          contato:   raw.contato?.celular || raw.contato?.telefone || cont.celular || cont.telefone || '',
          email:     cont.email || '',
          documento: raw.contato?.numeroDocumento || cont.numeroDocumento || '',
          endereco:  enderecoInicial,
          bairro:    endCont.bairro    || '',
          cidade:    endCont.municipio || '',
          cep:       endCont.cep       || '',
          uf:        endCont.uf        || '',
        },
        itens: [],
        observacoes: '',
      })
    }

    // 2. Busca detalhes completos na API (inclui itens, parcelas, vendedor, etc.)
    if (!isBlingConnected()) { setCarregando(false); return }

    buscarPedidoBling(blingId)
      .then(d => {
        if (!d) return
        // Salva detalhes completos no cache para Logística e Kanban
        saveBlingPedidoDetalhe(blingId, d)
        const total     = Number(d.totalVenda ?? d.total ?? d.totais?.total ?? 0)
        const prazo     = d.dataPrevista && d.dataPrevista !== '0000-00-00' ? d.dataPrevista : null
        const dataSaida = d.dataSaida    && d.dataSaida    !== '0000-00-00' ? d.dataSaida    : null

        // Endereço: API primeiro, fallback para store de contatos (endpoint de pedido não retorna endereço)
        const endApi  = d.contato?.endereco?.geral || {}
        const end     = Object.keys(endApi).length > 0 ? endApi : endCont
        const endereco = [end.endereco, end.numero, end.complemento, end.bairro, end.municipio]
          .filter(Boolean).join(', ') || enderecoInicial

        // Parcelas
        const parcelas = (d.parcelas || []).map(p => ({
          vencimento: p.dataVencimento,
          valor:      Number(p.valor || 0),
          obs:        p.observacoes || '',
        }))

        setPedido({
          id:          idCompleto,
          numero:      d.numeroLoja || `BL-${d.numero}`,
          numeroErp:   d.numero,
          dataVenda:   d.data || null,
          prazo,
          dataSaida,
          valorTotal:  total,
          totalDesconto: Number(d.totalDesconto || 0),
          status:           statusOver || (prazo && prazo < '2026-04-20' ? 'ENTREGUE' : 'CADASTRADO'),
          ajuste:           log.ajuste            || null,
          historicoAjustes: log.historicoAjustes  || [],
          cliente: {
            nome:      d.contato?.nome            || cont.nome || '—',
            contato:   d.contato?.celular         || d.contato?.telefone || cont.celular || cont.telefone || '',
            email:     d.contato?.email           || cont.email || '',
            documento: d.contato?.numeroDocumento || cont.numeroDocumento || '',
            tipo:      d.contato?.tipoPessoa,
            endereco,
            cep:       end.cep || '',
            uf:        end.uf  || '',
          },
          vendedor:      d.vendedor?.nome         || '',
          formaPagamento: d.formaPagamento?.descricao || '',
          itens:         d.itens                  || [],
          parcelas,
          observacoes:   d.observacoes            || '',
          obsInterna:    d.observacaoInterna       || '',
          situacao:      d.situacao,
        })
      })
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false))
  }, [blingId])

  if (!pedido && carregando) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <RefreshCw size={24} className="animate-spin text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-400">Carregando pedido do Bling…</p>
      </div>
    )
  }
  if (!pedido) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-sm text-slate-400">Pedido não encontrado.</p>
        <button onClick={() => navigate('/pedidos')} className="btn-secondary mt-4 text-sm mx-auto">Voltar</button>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">

      {/* Banner Bling — mesmo estilo do banner de sincronização */}
      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm font-medium flex items-center gap-2 text-blue-700">
        <CheckCircle2 size={15} className="shrink-0" />
        <span className="flex-1">
          Importado do Bling ERP{pedido.numeroErp ? ` — pedido #${pedido.numeroErp}` : ''}
        </span>
        {carregando && <RefreshCw size={13} className="animate-spin text-blue-400 shrink-0" />}
        {erro && <AlertCircle size={13} className="text-amber-500 shrink-0" title={erro} />}
      </div>

      {/* Header — idêntico ao pedido local */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/pedidos')} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Pedido {pedido.numero}</h1>
            <p className="text-sm text-slate-400">
              {pedido.dataVenda && <>Venda: {fmtDate(pedido.dataVenda)} · </>}
              Prazo: {fmtDate(pedido.prazo)}
              {pedido.dataSaida && <> · Saída: {fmtDate(pedido.dataSaida)}</>}
            </p>
            {pedido.criadoEm && (
              <p className="text-xs text-slate-400 mt-0.5">
                Cadastrado em {fmtDateTime(pedido.criadoEm)}
              </p>
            )}
          </div>
        </div>
        {/* Sem botões de ação pois é somente leitura */}
        <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-blue-100 text-blue-600 shrink-0 self-center">
          Bling ERP
        </span>
      </div>

      {/* Botão de abrir ajuste — cria pedido fantasma LOCAL a partir do Bling.
          Bling original fica intacto. */}
      <BotaoAbrirAjuste
        pedido={pedido}
        onCriar={dados => {
          const fantasma = criarPedidoAjuste(pedido, dados)
          savePedido(fantasma)
          navigate(`/pedidos/${fantasma.id}`)
        }}
      />

      {/* Cliente */}
      <div className="card p-5 mb-4">
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Cliente</h3>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <div>
            <span className="text-slate-400 text-xs block">Nome</span>
            <span className="font-medium">{pedido.cliente.nome}</span>
          </div>
          {pedido.cliente.documento && (
            <div>
              <span className="text-slate-400 text-xs block">Documento</span>
              <span className="font-medium">
                {pedido.cliente.documento}
                {pedido.cliente.tipo && <span className="ml-1 text-slate-400">({pedido.cliente.tipo === 'F' ? 'CPF' : pedido.cliente.tipo === 'J' ? 'CNPJ' : pedido.cliente.tipo})</span>}
              </span>
            </div>
          )}
          {pedido.cliente.contato && (
            <div>
              <span className="text-slate-400 text-xs block">Contato</span>
              <span className="font-medium">{formatTelefone(pedido.cliente.contato)}</span>
            </div>
          )}
          {pedido.cliente.email && (
            <div>
              <span className="text-slate-400 text-xs block">E-mail</span>
              <span className="font-medium">{pedido.cliente.email}</span>
            </div>
          )}
          {pedido.vendedor && (
            <div>
              <span className="text-slate-400 text-xs block">Vendedor</span>
              <span className="font-medium">{pedido.vendedor}</span>
            </div>
          )}
          {pedido.formaPagamento && (
            <div>
              <span className="text-slate-400 text-xs block">Forma de pagamento</span>
              <span className="font-medium">{pedido.formaPagamento}</span>
            </div>
          )}
          {pedido.cliente.endereco && (
            <div className="col-span-2">
              <span className="text-slate-400 text-xs block">Endereço de entrega</span>
              <span className="font-medium">{pedido.cliente.endereco}</span>
            </div>
          )}
          {(pedido.cliente.cep || pedido.cliente.uf) && (
            <div>
              <span className="text-slate-400 text-xs block">CEP / UF</span>
              <span className="font-medium">{[pedido.cliente.cep, pedido.cliente.uf].filter(Boolean).join(' · ')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Itens — mesmo card "Móveis do pedido" */}
      <div className="card p-5 mb-4">
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
          {carregando && !pedido.itens.length
            ? <span className="flex items-center gap-2"><RefreshCw size={11} className="animate-spin" /> Carregando itens…</span>
            : `Itens do pedido (${pedido.itens.length})`
          }
        </h3>

        {pedido.itens.length === 0 && !carregando ? (
          <p className="text-sm text-slate-400 py-2">Itens não disponíveis</p>
        ) : (
          <div className="space-y-3">
            {pedido.itens.map((item, i) => (
              <div key={item.id || i} className="border border-slate-100 rounded-xl p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-[#FFF9C2] text-[#CC8800] flex items-center justify-center text-sm font-bold shrink-0">
                      {i + 1}
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">{item.descricao || item.codigo || 'Item'}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {item.quantidade > 1 && (
                          <span>Qtd: <strong>{item.quantidade}</strong></span>
                        )}
                        {item.codigo && (
                          <span className="ml-2 font-mono text-slate-400">#{item.codigo}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-base font-bold text-[#CC8800]">
                      {fmt(item.valor * (item.quantidade || 1))}
                    </span>
                    {item.quantidade > 1 && (
                      <p className="text-[10px] text-slate-400 mt-0.5">{fmt(item.valor)} × {item.quantidade}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 pt-3 border-t border-slate-100 space-y-1.5">
          {pedido.desconto > 0 && (() => {
            const base = (Number(pedido.valorTotal) || 0) + (Number(pedido.desconto) || 0)
            const pct = base > 0 ? (pedido.desconto / base * 100).toFixed(1) : '—'
            return (
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Desconto aplicado</span>
                <span className="text-green-600 font-semibold">
                  − {fmt(pedido.desconto)} ({pct}%)
                </span>
              </div>
            )
          })()}
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Móveis</span>
            <span className="text-slate-700 font-medium">{fmt(pedido.valorTotal)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">
              Frete
              {pedido.freteLocal?.trim() && <span className="text-slate-400 ml-1">· {pedido.freteLocal.trim()}</span>}
            </span>
            <span className="text-slate-700 font-medium">
              {Number(pedido.frete) > 0 ? fmt(pedido.frete) : 'Grátis'}
            </span>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <span className="text-sm font-bold text-slate-700">Total do pedido</span>
            <span className="text-2xl font-black text-[#CC8800]">
              {fmt((Number(pedido.valorTotal) || 0) + (Number(pedido.frete) || 0))}
            </span>
          </div>
        </div>
      </div>

      {/* Parcelas */}
      {pedido.parcelas?.length > 0 && (
        <div className="card p-5 mb-4">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
            Parcelas ({pedido.parcelas.length})
          </h3>
          <div className="space-y-1">
            {pedido.parcelas.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-sm py-2 border-b border-slate-50 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-xs font-bold flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <div>
                    <span className="text-slate-700 font-medium">{fmtDate(p.vencimento)}</span>
                    {p.obs && <span className="ml-2 text-xs text-slate-400">{p.obs}</span>}
                  </div>
                </div>
                <span className="font-semibold text-slate-800">{fmt(p.valor)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(pedido.observacoes || pedido.obsInterna) && (
        <div className="card p-5 space-y-3">
          {pedido.observacoes && (
            <div>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Observações</h3>
              <p className="text-sm text-slate-700">{pedido.observacoes}</p>
            </div>
          )}
          {pedido.obsInterna && (
            <div>
              <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Observação interna</h3>
              <p className="text-sm text-slate-700">{pedido.obsInterna}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Detalhe de pedido local (Mini Deck) ───────────────────────────────────────

// Status em que o pedido está em produção — vendedora vira read-only,
// só gerente/admin pode editar. Evita mudança "calada" durante fabricação.
const STATUS_LOCKED_PRA_VENDEDORA = new Set(['FABRICANDO', 'EXPEDICAO', 'ROTA', 'ENTREGUE', 'POS_VENDA', 'AJUSTE'])

function LocalOrderDetail() {
  const { id }     = useParams()
  const navigate   = useNavigate()
  const location   = useLocation()
  const { moverStatus } = useOrders()
  const { profile } = useAuth()
  // Papéis com privilégio de edição sempre. Vendedora só edita se status
  // permite (não travado). Admin/gerente edita em qualquer status.
  const ehGerente  = profile?.role === 'admin' || profile?.role === 'gerente'
  const [pedido,      setPedido]      = useState(null)
  const [showStatus,  setShowStatus]  = useState(false)
  const [blingEnvio,  setBlingEnvio]  = useState(null)
  const [blingMsg,    setBlingMsg]    = useState('')
  const [dividirOpen, setDividirOpen] = useState(false)
  // Filhos do pedido (se for mãe dividida)
  const [filhos,      setFilhos]      = useState([])
  // Modal de edição do cliente (botão "Editar" no card cliente)
  const [editandoCliente, setEditandoCliente] = useState(false)

  // Lock síncrono: bloqueia imediatamente ao primeiro clique, antes do
  // setState fazer re-render. Resolve race condition do duplo-clique rápido.
  //
  // CRÍTICO: este useRef DEVE ficar acima de qualquer early return. Antes
  // ele estava depois do `if (!pedido) return null`, o que causava React
  // error #310 (rendered more hooks than during previous render): no
  // primeiro render pedido é null e zero hooks abaixo do return rodam;
  // no segundo render pedido tem valor e o useRef é chamado, mudando o
  // número total de hooks. Resultado: tela branca catastrófica.
  const enviandoBlingRef = useRef(false)

  useEffect(() => {
    const todos = getPedidos()
    const p     = todos.find(x => x.id === id)
    if (!p) navigate('/pedidos')
    else {
      setPedido(p)
      // Se for mãe dividida, carrega os filhos pra mostrar links
      if (p.dividido && Array.isArray(p.filhosIds)) {
        setFilhos(obterFilhos(p, todos))
      } else {
        setFilhos([])
      }
    }
  }, [id, navigate])

  if (!pedido) return null

  // Vendedora não edita pedido em produção (evita mudança "calada" durante fabricação).
  // Gerente/admin sempre podem editar.
  const travadoPraVendedora = !ehGerente && STATUS_LOCKED_PRA_VENDEDORA.has(pedido.status)

  const blingToken = isBlingConnected()

  async function enviarBling() {
    // Trava A.1: pedido já tem blingId — abortar
    if (pedido.blingId) return
    // Trava A.2: requisição em andamento (ref síncrono, sem race)
    if (enviandoBlingRef.current) return

    // Validação pré-envio: CPF/CNPJ com dígito verificador errado faz o
    // Bling responder "Não foi possível salvar o contato" sem dizer qual
    // campo está errado. Checa local antes pra dar mensagem clara.
    const tipoDoc = pedido.cliente?.tipoDocumento
    const doc     = pedido.cliente?.documento
    if (doc && !validarDocumento(doc, tipoDoc)) {
      setBlingEnvio('erro')
      setBlingMsg(`${tipoDoc} do cliente está inválido (dígito verificador errado). Edite o cadastro do cliente e tente de novo.`)
      return
    }

    enviandoBlingRef.current = true
    setBlingEnvio('enviando')
    setBlingMsg('Verificando se já existe no Bling…')

    try {
      // Proteção B: ANTES de criar, busca por numeroLoja no Bling.
      // Cobre o caso de duplicação por erro de rede (request mandado mas
      // resposta perdida — o pedido foi criado mas o blingId não foi salvo).
      let existente = null
      try {
        existente = await buscarPedidoBlingPorNumeroLoja(pedido.numero)
      } catch (_) {
        // Se a busca falhar (sem token, rate limit etc), segue pra criação —
        // melhor tentar enviar do que travar o gestor.
      }

      if (existente) {
        // Já existe no Bling — vincula em vez de criar de novo
        const atualizado = {
          ...pedido,
          blingId:     existente.id,
          blingNumero: existente.numero,
          blingErro:   undefined,
        }
        savePedido(atualizado)
        setPedido(atualizado)
        setBlingEnvio('ok')
        setBlingMsg(`Já existia no Bling — vinculado ao #${existente.numero}. Nenhum pedido duplicado foi criado.`)
        return
      }

      // Não existe: pode criar
      setBlingMsg('Enviando…')
      const data = await criarPedidoVendaBling(pedido)
      const atualizado = { ...pedido, blingId: data?.id, blingNumero: data?.numero, blingErro: undefined }
      savePedido(atualizado)
      setPedido(atualizado)
      setBlingEnvio('ok')
      setBlingMsg(`Pedido de venda criado no Bling${data?.numero ? ` — #${data.numero}` : ''}`)
    } catch (err) {
      const atualizado = { ...pedido, blingErro: err.message }
      savePedido(atualizado)
      setPedido(atualizado)
      setBlingEnvio('erro')
      setBlingMsg(err.message)
    } finally {
      enviandoBlingRef.current = false
    }
  }

  function mudarStatus(status) {
    moverStatus(pedido.id, status)
    setPedido(prev => ({ ...prev, status }))
    setShowStatus(false)
  }

  // Renumerar pedido — resolve colisão de número entre LOCAL e BLING.
  // Caso real: pedido LOCAL 2026-0085 (Thyara) e BLING numeroLoja=2026-0085
  // (Paula) — a dedup do useOrders esconde um em favor do outro. Mudar
  // o número de um quebra a colisão. Valida que o novo número não está
  // em uso por outro pedido local.
  function renumerar() {
    const novo = window.prompt(
      `Pedido atual: ${pedido.numero}\n\n` +
      `Digite o NOVO número do pedido (ex: ${pedido.numero}-T pra resolver colisão com Bling):`,
      pedido.numero
    )
    if (!novo || novo === pedido.numero) return
    const novoTrim = novo.trim()
    if (!novoTrim) return
    // Confere colisão com outro local (incluindo lixeira — pedido deletado
    // pode ser restaurado e aí teríamos 2 vivos com mesmo número) E com
    // histórico do Bling (justamente o caso de uso que motiva renumerar).
    const todosLocais = getPedidosRaw()
    const conflitoLocal = todosLocais.find(x => x.id !== pedido.id && String(x.numero) === novoTrim)
    if (conflitoLocal) {
      const sufixo = conflitoLocal.deletadoEm ? ' [na lixeira]' : ''
      window.alert(`Já existe um pedido local com número "${novoTrim}"${sufixo} (cliente: ${conflitoLocal.cliente?.nome || '—'}). Escolha outro.`)
      return
    }
    const conflitoBling = getBlingHistorico().find(p => String(p.numeroLoja || p.numero || '') === novoTrim)
    if (conflitoBling) {
      const ok = window.confirm(`Esse número já existe no Bling (numeroLoja="${novoTrim}", cliente: ${conflitoBling.contato?.nome || '—'}). Isso vai recriar colisão. Renumerar mesmo assim?`)
      if (!ok) return
    }
    const atualizado = { ...pedido, numero: novoTrim }
    savePedido(atualizado)
    setPedido(atualizado)
    window.alert(`Pedido renumerado pra "${novoTrim}". Se havia colisão com o Bling, o pedido do Bling deve aparecer normalmente agora.`)
  }

  // Confirma a divisão: grava a mãe (com dividido=true) + os filhos
  function confirmarDivisao(partes) {
    const { mae, filhos: novosFilhos } = dividirPedido(pedido, partes)
    savePedido(mae)
    novosFilhos.forEach(f => savePedido(f))
    setPedido(mae)
    setFilhos(novosFilhos)
    setDividirOpen(false)
  }

  const isNovo = location.state?.novo

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">

      {isNovo && (
        <div className="mb-4 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 font-medium flex items-center gap-2">
          ✓ Pedido <strong>{pedido.numero}</strong> cadastrado com sucesso!
        </div>
      )}

      {/* Banner Bling */}
      {blingToken && (
        <div className={`mb-4 rounded-xl px-4 py-3 text-sm font-medium flex items-center gap-2
          ${blingEnvio === 'ok'   || pedido.blingId  ? 'bg-blue-50 border border-blue-200 text-blue-700'  : ''}
          ${blingEnvio === 'erro' || (pedido.blingErro && !pedido.blingId) ? 'bg-red-50 border border-red-200 text-red-700'   : ''}
          ${!blingEnvio && !pedido.blingId && !pedido.blingErro ? 'bg-slate-50 border border-slate-200 text-slate-500' : ''}
        `}>
          {(blingEnvio === 'ok' || pedido.blingId) && <CheckCircle2 size={15} className="shrink-0" />}
          {(blingEnvio === 'erro' || (pedido.blingErro && !pedido.blingId)) && <AlertCircle size={15} className="shrink-0" />}
          {blingEnvio === 'enviando' && <RefreshCw size={15} className="shrink-0 animate-spin" />}

          <span className="flex-1">
            {blingEnvio === 'enviando' && 'Enviando para o Bling...'}
            {blingEnvio === 'ok'       && blingMsg}
            {blingEnvio === 'erro'     && `Bling: ${blingMsg}`}
            {!blingEnvio && pedido.blingId  && `Sincronizado com Bling${pedido.blingNumero ? ` — pedido #${pedido.blingNumero}` : ''}`}
            {!blingEnvio && pedido.blingErro && !pedido.blingId && `Falha no Bling: ${pedido.blingErro}`}
            {!blingEnvio && !pedido.blingId && !pedido.blingErro && 'Não enviado ao Bling ainda'}
          </span>

          {!pedido.blingId && (
            <button
              onClick={enviarBling}
              disabled={blingEnvio === 'enviando'}
              className={`shrink-0 text-xs font-semibold rounded-lg px-3 py-1.5 transition-colors
                ${blingEnvio === 'enviando'
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  : pedido.blingErro
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
            >
              {blingEnvio === 'enviando'
                ? '⏳ Verificando…'
                : pedido.blingErro
                  ? 'Tentar novamente'
                  : 'Enviar agora'}
            </button>
          )}
          {pedido.blingId && (
            <span className="shrink-0 text-xs text-blue-500">✓ enviado #{pedido.blingNumero || ''}</span>
          )}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/pedidos')} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-800">Pedido {pedido.numero}</h1>
              {!travadoPraVendedora && (
                <button
                  onClick={renumerar}
                  title="Renumerar pedido — resolve colisão de número com pedido do Bling"
                  className="text-[10px] font-bold text-slate-400 hover:text-[#CC8800] hover:bg-amber-50 px-1.5 py-0.5 rounded transition-colors"
                >
                  renumerar
                </button>
              )}
            </div>
            <p className="text-sm text-slate-400">
              {pedido.dataVenda && <>Venda: {fmtDate(pedido.dataVenda)} · </>}Prazo: {fmtDate(pedido.prazo)}
            </p>
            {pedido.criadoEm && (
              <p className="text-xs text-slate-400 mt-0.5">
                Cadastrado em {fmtDateTime(pedido.criadoEm)}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <button onClick={() => setShowStatus(!showStatus)} className="btn-secondary text-sm">
              <StatusBadge status={pedido.status} />
              <ChevronDown size={14} />
            </button>
            {showStatus && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-20 w-48">
                {KANBAN_COLUNAS.map(col => (
                  <button
                    key={col.id}
                    onClick={() => mudarStatus(col.id)}
                    className={`w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors
                      ${pedido.status === col.id ? 'font-semibold text-[#CC8800]' : 'text-slate-700'}`}
                  >
                    {col.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Dividir pedido — só pra pedidos não-divididos, não-filhos, não-fantasmas */}
          {!pedido.dividido && !pedido.tipoFilho && !pedido.tipoAjuste && pedido.moveis?.length >= 2 && (
            <button
              onClick={() => setDividirOpen(true)}
              className="btn-secondary text-sm"
              title="Dividir pedido em 2/3/4 filhos (1234/A, /B, /C)"
            >
              <Split size={15} />
              Dividir
            </button>
          )}
          <button onClick={() => gerarPDF(pedido)} className="btn-secondary text-sm">
            <FileDown size={15} />
            PDF
          </button>
          <button onClick={() => exportarPedidoXlsx(pedido)} className="btn-secondary text-sm">
            <Sheet size={15} />
            Excel
          </button>
          <button onClick={() => abrirWhatsApp(pedido)} className="btn-primary text-sm">
            <MessageCircle size={15} />
            WhatsApp
          </button>
        </div>
      </div>

      {/* Banner / botão de ajuste pós-venda */}
      {pedido.tipoAjuste ? (
        <PainelAjusteFantasma pedido={pedido} />
      ) : (
        <BotaoAbrirAjuste
          pedido={pedido}
          onCriar={dados => {
            const fantasma = criarPedidoAjuste(pedido, dados)
            savePedido(fantasma)
            navigate(`/pedidos/${fantasma.id}`)
          }}
        />
      )}

      {/* Banner: este pedido é um FILHO de uma divisão */}
      {pedido.tipoFilho && (
        <div className="card p-4 mb-4 border-2 border-indigo-200 bg-indigo-50/40">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <Split size={14} className="text-indigo-600" />
              <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wide bg-indigo-200 text-indigo-900">
                PARTE {pedido.letraParte} DE {pedido.totalPartes}
              </span>
              <span className="text-sm font-bold text-slate-800">
                Subset do pedido <strong>#{pedido.pedidoMaeNumero}</strong>
              </span>
              <span className="text-xs text-slate-500">
                · {pedido.moveis?.length || 0} móve{pedido.moveis?.length === 1 ? 'l' : 'is'}
              </span>
            </div>
            {pedido.pedidoMaeId && (
              <button
                onClick={() => navigate(`/pedidos/${pedido.pedidoMaeId}`)}
                className="text-xs font-semibold text-[#CC8800] hover:underline"
              >
                Abrir pedido mãe →
              </button>
            )}
          </div>
        </div>
      )}

      {/* Banner: este pedido é uma MÃE dividida com N filhos */}
      {pedido.dividido && filhos.length > 0 && (() => {
        const ag = statusAgregadoFilhos(filhos)
        return (
          <div className="card p-4 mb-4 border-2 border-indigo-200 bg-indigo-50/40">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Split size={14} className="text-indigo-600" />
              <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wide bg-indigo-200 text-indigo-900">
                DIVIDIDO EM {filhos.length} PARTES
              </span>
              <span className="text-xs text-slate-500">
                · {ag.entregues}/{ag.total} entregue{ag.entregues === 1 ? '' : 's'}
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {filhos.map(f => (
                <button
                  key={f.id}
                  onClick={() => navigate(`/pedidos/${f.id}`)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200 hover:border-indigo-400 transition-colors text-left"
                >
                  <span className="w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-black shrink-0">
                    {f.letraParte}
                  </span>
                  <div className="min-w-0">
                    <p className="text-[11px] font-bold text-slate-800 truncate">{f.numero}</p>
                    <p className="text-[9px] text-slate-400 uppercase tracking-wide truncate">{f.status}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )
      })()}

      {/* Modal de divisão */}
      {dividirOpen && (
        <DividirPedidoModal
          pedido={pedido}
          onClose={() => setDividirOpen(false)}
          onConfirm={confirmarDivisao}
        />
      )}

      {/* Banner de bloqueio pra vendedora quando pedido tá em produção */}
      {travadoPraVendedora && (
        <div className="mb-4 rounded-xl border-2 border-red-200 bg-red-50 p-3 flex items-start gap-3">
          <Lock size={16} className="text-red-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-bold text-red-800">
              Pedido em produção — só gerente/admin pode editar
            </p>
            <p className="text-xs text-red-700 mt-0.5 leading-relaxed">
              Se precisar corrigir algo neste pedido, fale com o gerente. Editar depois que a fábrica começou pode causar erro de produção.
            </p>
          </div>
        </div>
      )}

      {/* Cliente */}
      <div className="card p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Cliente</h3>
          {!travadoPraVendedora && (
            <button
              onClick={() => setEditandoCliente(true)}
              title="Editar nome, CPF/CNPJ, contato ou e-mail"
              className="flex items-center gap-1 text-xs font-semibold text-[#CC8800] hover:text-[#A06800] px-2 py-1 rounded-lg hover:bg-amber-50 transition-colors"
            >
              <Pencil size={12} />
              Editar
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <div>
            <span className="text-slate-400 text-xs block">Nome</span>
            <span className="font-medium">{pedido.cliente?.nome || '—'}</span>
          </div>
          <div>
            <span className="text-slate-400 text-xs block">Documento</span>
            <span className="font-medium">{pedido.cliente?.documento || '—'}{pedido.cliente?.tipoDocumento ? ` (${pedido.cliente.tipoDocumento})` : ''}</span>
          </div>
          <div>
            <span className="text-slate-400 text-xs block">Contato</span>
            <span className="font-medium">{formatTelefone(pedido.cliente?.contato) || '—'}</span>
          </div>
          <div>
            <span className="text-slate-400 text-xs block">E-mail</span>
            <span className="font-medium">{pedido.cliente?.email || '—'}</span>
          </div>
          {pedido.vendedora && (
            <div>
              <span className="text-slate-400 text-xs block">Vendedora</span>
              <span className="font-medium">{pedido.vendedora}</span>
            </div>
          )}
          {pedido.pagamento?.dividido ? (
            <div className="col-span-2">
              <span className="text-slate-400 text-xs block">Formas de pagamento (dividido)</span>
              <div className="space-y-0.5 mt-0.5 text-sm">
                {pedido.pagamento.entrada?.valor > 0 && (
                  <div>
                    <span className="text-slate-500">Entrada:</span>{' '}
                    <span className="font-medium">{fmt(pedido.pagamento.entrada.valor)}</span>
                    {pedido.pagamento.entrada.forma && <span className="text-slate-500"> · {FORMAS_LABEL[pedido.pagamento.entrada.forma] || pedido.pagamento.entrada.forma}</span>}
                    {pedido.pagamento.entrada.obs && <span className="text-slate-400 italic"> · {pedido.pagamento.entrada.obs}</span>}
                  </div>
                )}
                {pedido.pagamento.saldo?.valor > 0 && (
                  <div>
                    <span className="text-slate-500">Saldo:</span>{' '}
                    <span className="font-medium">{fmt(pedido.pagamento.saldo.valor)}</span>
                    {pedido.pagamento.saldo.forma && <span className="text-slate-500"> · {FORMAS_LABEL[pedido.pagamento.saldo.forma] || pedido.pagamento.saldo.forma}</span>}
                    {pedido.pagamento.saldo.obs && <span className="text-slate-400 italic"> · {pedido.pagamento.saldo.obs}</span>}
                  </div>
                )}
              </div>
            </div>
          ) : (
            pedido.formaPagamento && (
              <div>
                <span className="text-slate-400 text-xs block">Forma de pagamento</span>
                <span className="font-medium">{FORMAS_LABEL[pedido.formaPagamento] || pedido.formaPagamento}</span>
              </div>
            )
          )}
          <div>
            <span className="text-slate-400 text-xs block">Frete</span>
            <span className="font-medium">
              {pedido.frete > 0 ? fmt(pedido.frete) : 'Grátis'}
              {pedido.freteLocal?.trim() && <span className="text-slate-400 font-normal ml-1">· {pedido.freteLocal.trim()}</span>}
            </span>
          </div>
          {pedido.valorPendenteEntrega > 0 && (
            <div>
              <span className="text-slate-400 text-xs block">Pendente na entrega</span>
              <span className="font-semibold text-orange-600">{fmt(pedido.valorPendenteEntrega)}</span>
            </div>
          )}
          {pedido.prazoPagamento && (
            <div className="col-span-2">
              <span className="text-slate-400 text-xs block">Condição de pagamento</span>
              <span className="font-medium">
                {pedido.prazoPagamento === 'custom'
                  ? pedido.prazoPagamentoCustom || 'Personalizado'
                  : PRAZOS_LABEL[pedido.prazoPagamento] || pedido.prazoPagamento}
              </span>
            </div>
          )}
          <div className="col-span-2">
            <span className="text-slate-400 text-xs block">Endereço de entrega</span>
            <span className="font-medium">{pedido.cliente?.endereco || '—'}</span>
          </div>
          {(pedido.cliente?.cep || pedido.cliente?.uf) && (
            <div>
              <span className="text-slate-400 text-xs block">CEP / UF</span>
              <span className="font-medium">
                {[pedido.cliente?.cep, pedido.cliente?.uf].filter(Boolean).join(' · ')}
              </span>
            </div>
          )}
          {pedido.cliente?.tipoDocumento === 'CNPJ' && (
            <div>
              <span className="text-slate-400 text-xs block">ICMS</span>
              <span className="font-medium capitalize">
                {pedido.cliente.contribuinteICMS === 'sim' ? 'Contribuinte ICMS' : 'Não contribuinte'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Móveis */}
      <div className="card p-5 mb-4">
        <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">
          Móveis do pedido ({pedido.moveis?.length || 0})
        </h3>
        <div className="space-y-3">
          {(pedido.moveis || []).map((m, i) => (
            <div key={m.id} className="border border-slate-100 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#FFF9C2] text-[#CC8800] flex items-center justify-center text-sm font-bold shrink-0">
                    {i + 1}
                  </div>
                  <p className="text-sm text-slate-700 leading-relaxed">{descricaoMovel(m)}</p>
                </div>
                <span className="shrink-0 text-right">
                  {m.precoOriginal && m.precoOriginal !== m.preco && (
                    <span className="block text-xs line-through text-slate-400">{fmt(m.precoOriginal)}</span>
                  )}
                  <span className="text-base font-bold text-[#CC8800]">{fmt(m.preco)}</span>
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 pt-3 border-t border-slate-100 space-y-1.5">
          {pedido.desconto > 0 && (() => {
            const base = (Number(pedido.valorTotal) || 0) + (Number(pedido.desconto) || 0)
            const pct = base > 0 ? (pedido.desconto / base * 100).toFixed(1) : '—'
            return (
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Desconto aplicado</span>
                <span className="text-green-600 font-semibold">
                  − {fmt(pedido.desconto)} ({pct}%)
                </span>
              </div>
            )
          })()}
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">Móveis</span>
            <span className="text-slate-700 font-medium">{fmt(pedido.valorTotal)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-500">
              Frete
              {pedido.freteLocal?.trim() && <span className="text-slate-400 ml-1">· {pedido.freteLocal.trim()}</span>}
            </span>
            <span className="text-slate-700 font-medium">
              {Number(pedido.frete) > 0 ? fmt(pedido.frete) : 'Grátis'}
            </span>
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <span className="text-sm font-bold text-slate-700">Total do pedido</span>
            <span className="text-2xl font-black text-[#CC8800]">
              {fmt((Number(pedido.valorTotal) || 0) + (Number(pedido.frete) || 0))}
            </span>
          </div>
        </div>
      </div>

      {pedido.observacoesGerais && (
        <div className="card p-5">
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Observações gerais</h3>
          <p className="text-sm text-slate-700">{pedido.observacoesGerais}</p>
        </div>
      )}

      {/* Histórico de auditoria — SÓ gerente/admin vê */}
      {ehGerente && (
        <div className="mt-4">
          <HistoricoPedido pedido={pedido} />
        </div>
      )}

      {/* Modal de edição do cliente */}
      {editandoCliente && (
        <EditarClienteModal
          pedido={pedido}
          onClose={() => setEditandoCliente(false)}
          onSalvar={(atualizado) => {
            savePedido(atualizado)
            setPedido(atualizado)
            setEditandoCliente(false)
          }}
        />
      )}
    </div>
  )
}

// ── Roteador: decide qual detalhe exibir ──────────────────────────────────────

export function OrderDetail() {
  const { id } = useParams()
  if (id?.startsWith('bling-')) {
    return <BlingOrderDetail blingId={id.replace('bling-', '')} />
  }
  return <LocalOrderDetail />
}
